import { HttpStatusCode } from 'axios';
import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';

import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import { Entity } from '../../../../../types/apiTypes';
import { LinkedAccount } from '../../../../../types/dbTypes';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import {
  Bank,
  CheckCircle,
  DotsThreeVertical,
  Plus,
} from '@phosphor-icons/react';
import { usePlaidLink } from 'react-plaid-link';
import { LinkMethodChooserModal } from '../banking/linkAccount/LinkMethodChooserModal';
import { ManualLinkModal } from '../banking/linkAccount/ManualLinkModal';
import { LinkMethod } from '../banking/linkAccount/types';

interface LinkedAccountsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  isPreActive: boolean;
}

function capitalize(s: string): string {
  return s.length === 0 ? '' : s[0].toUpperCase() + s.slice(1);
}

function formatLinkedAccountLine(account: LinkedAccount): string {
  const name = account?.bank_name ?? '';
  const mask = account?.bank_mask;
  const rawType = account?.bank_account_type ?? '';
  // Plaid accounts store "depository#checking"; manual accounts store "checking".
  const subtype = rawType.includes('#') ? rawType.split('#')[1] : rawType;
  const typeLabel = capitalize(subtype ?? '');
  const tail = mask ? `...${mask}` : '';
  const suffix = typeLabel ? ` (${typeLabel})` : '';
  return `${name}${tail}${suffix}`;
}

function LinkedAccounts({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  isPreActive,
}: LinkedAccountsProps) {
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const { userRecord, userId } = useUser();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [openLink, setOpenLink] = useState(false);
  const [linkMethodChooserOpen, setLinkMethodChooserOpen] = useState(false);
  const [manualLinkOpen, setManualLinkOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchLinkedAccounts();
    fetchLinkToken(
      userRecord?.userType === Entity.Customer ? userRecord?.customer_id : ''
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLinkToken = async (customer_id: string) => {
    try {
      const body = {
        owner_id: customer_id,
        entity: 'customer', // Get curr (Customer, Vendor, or Partner)
      };
      const response = await callProtectedApi(
        '/linkedAccounts/plaidLinkToken',
        'POST',
        body,
        'Error occurred while connecting to Plaid.',
        {},
        undefined,
        5
      );
      if (response?.data?.data?.token) {
        setLinkToken(response.data.data.token);
      }
    } catch (error: any) {
      console.error('Error fetching Plaid link token:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedAccounts = async () => {
    try {
      const response = await callProtectedApi(
        `/linkedAccounts/list/${Entity.Customer}/${userId ?? ''}`,
        'get',
        {},
        'Error occurred while fetching your linked accounts.'
      );
      const data = response?.data?.data?.linkedAccounts;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        setLinkedAccounts(data);
      }
    } catch (e: any) {
      console.error(
        'Error occurred while fetching your linked accounts: ',
        e?.message
      );
    } finally {
      setLoading(false);
    }
  };

  const createLinkedAccount = async (
    customer_id: string,
    public_token: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/linkedAccounts/${customer_id}`,
        'post',
        { entity: 'customer', public_token: public_token },
        'Error occurred while creating newly linked account record.'
      );
      if (response?.status === HttpStatusCode.Created) {
        await fetchLinkedAccounts();
      } else {
        toast.error('Failed to save newly linked account.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating newly linked account record: ',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = useCallback(
    async (public_token: string) => {
      try {
        await createLinkedAccount(userId ?? '', public_token);
        return;
      } catch (error: any) {
        console.error('Error adding payment method:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callProtectedApi]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken!,
    onSuccess,
  });

  useEffect(() => {
    if (openLink && ready) {
      open();
      setOpenLink(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLink, ready, open]);

  const togglePopup = (accountId: string) => {
    setActivePopup(activePopup === accountId ? null : accountId);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!activePopup) return;

    const clickedPopup = linkedAccounts.find((account) => {
      const popupRef = popupRefs.current[account?.account_id ?? ''];
      return popupRef?.contains(event.target as Node);
    });

    if (!clickedPopup) {
      setActivePopup(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePopup]);

  const setAccountAsPrimary = async (accountId: string) => {
    try {
      const response = await callProtectedApi(
        `/linkedAccounts/setPrimary/${Entity.Customer}/${userId}/${accountId}`,
        'post',
        {},
        'Error occurred while setting account as primary.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Account set as primary successfully');
        fetchLinkedAccounts();
      }
    } catch (e: any) {
      console.error(
        'Error occurred while setting account as primary: ',
        e?.message
      );
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full min-h-[80vh] w-full flex-col items-center justify-start gap-y-6 overflow-y-auto overflow-x-hidden border-[1px] border-[#EAECF0] bg-white px-2 py-6 md:p-6">
            <div className="flex w-full flex-col items-start justify-between space-y-2 align-middle md:w-[95%] md:flex-row md:items-center lg:w-[80%]">
              <h2 className="text-[22px] font-semibold text-primary">
                Linked Accounts
              </h2>
              <AccentButtonOutline
                onClick={() => setLinkMethodChooserOpen(true)}
                disabled={isPreActive}
                addClass="h-[32px] w-[204px] rounded-full justify-between items-center disabled:cursor-not-allowed disabled:bg-white disabled:opacity-60"
              >
                <Plus size={20} />{' '}
                <p className="text-[14px] font-[400] text-primary">
                  Link New Bank Account
                </p>
              </AccentButtonOutline>
            </div>
            {!loading ? (
              <div className="flex w-full flex-col items-center justify-center space-y-2">
                {linkedAccounts.length > 0 ? (
                  <div className="flex max-h-[60vh] w-full flex-col items-center gap-y-2 overflow-y-auto md:w-[95%] lg:w-[80%]">
                    {linkedAccounts.map((account: LinkedAccount) => (
                      <div
                        key={account?.account_id}
                        className="flex w-full flex-row items-center justify-between rounded-[4px] border-[1px] border-bordergray px-6 py-4"
                      >
                        <p>{formatLinkedAccountLine(account)}</p>
                        <div className="relative flex flex-row items-center justify-center space-x-2">
                          {account?.is_primary && (
                            <div className="flex flex-row items-center justify-center space-x-2">
                              <p className="text-[14px] font-[400] text-secondary">
                                Primary
                              </p>
                              <CheckCircle
                                weight="bold"
                                size={20}
                                className="text-secondary"
                              />
                            </div>
                          )}
                          <DotsThreeVertical
                            weight="bold"
                            size={24}
                            onClick={() =>
                              togglePopup(account?.account_id ?? '')
                            }
                            className="cursor-pointer"
                          />
                          {activePopup === account?.account_id && (
                            <div
                              ref={(el) => {
                                popupRefs.current[account?.account_id ?? ''] =
                                  el;
                              }}
                              className="absolute right-0 top-[1.5rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                            >
                              <div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAccountAsPrimary(
                                      account?.account_id ?? ''
                                    );
                                    togglePopup(account?.account_id ?? '');
                                  }}
                                  className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                >
                                  <CheckCircle size={20} />
                                  <p>Make Primary</p>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex w-full flex-col items-center justify-center space-y-4 py-16 md:w-[95%] lg:w-[80%]">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
                      <Bank
                        size={40}
                        weight="duotone"
                        className="text-gray-400"
                      />
                    </div>
                    <div className="flex flex-col items-center justify-center space-y-2 text-center">
                      <h3 className="text-[18px] font-semibold text-primary">
                        No Linked Bank Accounts
                      </h3>
                      <p className="max-w-md text-[14px] text-primary">
                        You do not have any linked bank accounts. Connect your
                        bank account to enable seamless transfers.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={screenView === 'mobile' ? 40 : 60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <>
      {userRecord &&
        userId &&
        userId?.length > 0 &&
        renderScreenView(screenView)}
      <LinkMethodChooserModal
        isOpen={linkMethodChooserOpen}
        onClose={() => setLinkMethodChooserOpen(false)}
        plaidReady={!!linkToken}
        onSelectMethod={(method) => {
          setLinkMethodChooserOpen(false);
          if (method === LinkMethod.Plaid) {
            setOpenLink(true);
          } else {
            setManualLinkOpen(true);
          }
        }}
      />
      <ManualLinkModal
        isOpen={manualLinkOpen}
        onClose={() => setManualLinkOpen(false)}
        ownerId={userId ?? ''}
        entity={Entity.Customer}
        onSuccess={fetchLinkedAccounts}
      />
    </>
  );
}

export default LinkedAccounts;
